import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  TextInput,
  Platform,
  RefreshControl,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import { useDispatch } from 'react-redux';
import { BottomSheetModal } from '../components/BottomSheet';
import type { BottomSheetModalRef } from '../components/BottomSheet';
import type { RootStackParamList } from '../navigation/stackTypes';
import type { AppDispatch } from '../store';
import { isSuccessful } from '../services/api-requests';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { getMyRedeemedCodes, redeemPricingCode } from '../services/pricing-code-service';
import { updateSelectedCharger } from '../store/actions/charging-actions';
import type { RedeemedCode } from '../models/pricing-code-model';
import { formatDiscountLabel, DISCOUNT_BADGE_COLORS } from '../models/pricing-group-model';
import { showThemedAlert } from '../components/common/ThemedAlert';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'RedeemedCodes'>;

export default function RedeemedCodes() {
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch<AppDispatch>();

  const [codes, setCodes] = useState<RedeemedCode[]>([]);
  const [navigatingChargerId, setNavigatingChargerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [redeemInput, setRedeemInput] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [selectedCode, setSelectedCode] = useState<RedeemedCode | null>(null);

  const bottomSheetRef = useRef<BottomSheetModalRef>(null);
  const snapPoints = useMemo(() => ['75%', '90%'], []);
  const [sheetSnapIndex, setSheetSnapIndex] = useState(0);

  const screenHeight = Dimensions.get('window').height;
  const extraPadding = sheetSnapIndex === 0
    ? screenHeight * 0.15
    : Platform.OS === 'ios' ? 40 : 24;

  const fetchCodes = useCallback(async () => {
    try {
      const response = await getMyRedeemedCodes();
      if (isSuccessful(response.status) && response.payload) {
        setCodes(response.payload.redeemed_codes ?? []);
      } else {
        showThemedAlert('Error', 'Unable to load your redeemed codes.');
      }
    } catch {
      showThemedAlert('Error', 'Something went wrong while loading redeemed codes.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchCodes();
      setLoading(false);
    })();
  }, [fetchCodes]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCodes();
    setRefreshing(false);
  }, [fetchCodes]);

  const handleRedeem = useCallback(async () => {
    const trimmed = redeemInput.trim();
    if (!trimmed) {
      showThemedAlert('Invalid Code', 'Please enter a pricing code.');
      return;
    }
    setRedeeming(true);
    try {
      const response = await redeemPricingCode(trimmed);
      if (isSuccessful(response.status)) {
        showThemedAlert('Success', response.payload?.message ?? 'Code redeemed successfully!');
        setRedeemInput('');
        await fetchCodes();
      } else {
        showThemedAlert('Error', 'Unable to redeem this code. It may be invalid or expired.');
      }
    } catch {
      showThemedAlert('Error', 'Something went wrong. Please try again.');
    } finally {
      setRedeeming(false);
    }
  }, [redeemInput, fetchCodes]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderRedeemSection = () => (
    <View style={styles.redeemSection}>
      <Text style={styles.sectionTitle}>Redeem a Code</Text>
      <View style={styles.redeemRow}>
        <TextInput
          style={styles.redeemInput}
          placeholder="Enter pricing code"
          placeholderTextColor="#8D8E90"
          value={redeemInput}
          onChangeText={setRedeemInput}
          autoCapitalize="characters"
          autoCorrect={false}
        />
        <LoadingButton
          onPress={handleRedeem}
          isLoading={redeeming}
          disabled={!redeemInput.trim()}
          backgroundColor="#40BFB4"
          style={styles.redeemButton}
          textStyle={styles.redeemButtonText}
        >
          Redeem
        </LoadingButton>
      </View>
    </View>
  );

  const renderCodeCard = (item: RedeemedCode) => {
    const badgeColor = DISCOUNT_BADGE_COLORS[item.discount_type];
    return (
      <Pressable
        key={item.redemption_id}
        style={({ pressed }) => [styles.card, { opacity: pressed ? 0.85 : 1 }]}
        onPress={() => { setSelectedCode(item); bottomSheetRef.current?.present(); }}
      >
        <View style={styles.cardHeader}>
          <View style={[styles.discountBadge, { backgroundColor: badgeColor + '20' }]}>
            <Text style={[styles.discountBadgeText, { color: badgeColor }]}>
              {formatDiscountLabel(item.discount_type, item.discount_value)}
            </Text>
          </View>
          <MaterialIcons name="chevron-right" size={22} color="#8D8E90" />
        </View>
        <Text style={styles.codeName}>{item.code_name}</Text>
        <Text style={styles.codeString}>{item.code}</Text>
        <View style={styles.cardMeta}>
          <Text style={styles.metaText}>Host: {item.host_name}</Text>
          <Text style={styles.metaText}>
            {item.expires_at ? `Expires ${formatDate(item.expires_at)}` : 'No expiration'}
          </Text>
        </View>
        <Text style={styles.redeemedAt}>Redeemed {formatDate(item.redeemed_at)}</Text>
      </Pressable>
    );
  };

  const handleChargerPress = async (chargerId: string) => {
    setNavigatingChargerId(chargerId);
    try {
      await dispatch(updateSelectedCharger(chargerId));
      bottomSheetRef.current?.dismiss();
      navigation.navigate('ChargerStation', {});
    } catch {
      showThemedAlert('Error', 'Unable to load charger details. Please try again.');
    } finally {
      setNavigatingChargerId(null);
    }
  };

  const renderDetailSheet = () => {
    const badgeColor = selectedCode
      ? DISCOUNT_BADGE_COLORS[selectedCode.discount_type]
      : '#40BFB4';
    return (
      <BottomSheetModal
        ref={bottomSheetRef}
        index={0}
        snapPoints={snapPoints}
        enablePanDownToClose
        enableDynamicSizing={false}
        onChange={(index: number) => setSheetSnapIndex(index)}
        onDismiss={() => { setSelectedCode(null); setSheetSnapIndex(0); }}
        backgroundStyle={styles.sheetBackground}
        handleIndicatorStyle={styles.sheetHandle}
        style={Platform.OS === 'web' ? styles.sheetWeb : undefined}
      >
        {selectedCode && (
          <View style={styles.sheetInner}>
            <Text style={styles.modalTitle}>{selectedCode.code_name}</Text>

            <View style={[styles.modalBadge, { backgroundColor: badgeColor + '20' }]}>
              <Text style={[styles.modalBadgeText, { color: badgeColor }]}>
                {formatDiscountLabel(selectedCode.discount_type, selectedCode.discount_value)}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Code</Text>
              <Text style={styles.detailValue}>{selectedCode.code}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Host</Text>
              <Text style={styles.detailValue}>{selectedCode.host_name}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Expires</Text>
              <Text style={styles.detailValue}>
                {selectedCode.expires_at ? formatDate(selectedCode.expires_at) : 'No expiration'}
              </Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Redeemed</Text>
              <Text style={styles.detailValue}>{formatDate(selectedCode.redeemed_at)}</Text>
            </View>

            {selectedCode.chargers && selectedCode.chargers.length > 0 && (
              <View style={styles.chargersSection}>
                <Text style={styles.chargersSectionTitle}>
                  Eligible Chargers ({selectedCode.chargers.length})
                </Text>
                  <ScrollView
                    style={styles.chargerScrollView}
                    contentContainerStyle={{ paddingBottom: extraPadding }}
                    nestedScrollEnabled
                    showsVerticalScrollIndicator
                  >
                  {selectedCode.chargers.map((charger) => {
                    const isNavigating = navigatingChargerId === charger.charger_id;
                    return (
                      <Pressable
                        key={charger.charger_id}
                        style={({ pressed }) => [
                          styles.chargerItem,
                          { opacity: pressed ? 0.7 : 1 },
                        ]}
                        onPress={() => handleChargerPress(charger.charger_id)}
                        disabled={!!navigatingChargerId}
                      >
                        <View style={styles.chargerIconContainer}>
                          <Ionicons name="flash" size={18} color="#40BFB4" />
                        </View>
                        <View style={styles.chargerDetails}>
                          <Text style={styles.chargerName} numberOfLines={1}>
                            {charger.charger_name ?? 'Charger'}
                          </Text>
                          {charger.full_address && (
                            <Text style={styles.chargerAddress} numberOfLines={1}>
                              {charger.full_address}
                            </Text>
                          )}
                          <View style={styles.chargerStatusRow}>
                            <View
                              style={[
                                styles.chargerAvailDot,
                                { backgroundColor: charger.is_enabled !== false ? '#10B981' : '#EF4444' },
                              ]}
                            />
                            <Text
                              style={[
                                styles.chargerAvailText,
                                { color: charger.is_enabled !== false ? '#10B981' : '#EF4444' },
                              ]}
                            >
                              {charger.is_enabled !== false ? 'Available' : 'Unavailable'}
                            </Text>
                          </View>
                        </View>
                        {isNavigating ? (
                          <ActivityIndicator size="small" color="#40BFB4" />
                        ) : (
                          <MaterialIcons name="chevron-right" size={20} color="#8D8E90" />
                        )}
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            )}
          </View>
        )}
      </BottomSheetModal>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <Header headerText="Redeemed Codes" />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor="#40BFB4"
          />
        }
      >
        {renderRedeemSection()}

        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color="#40BFB4" />
          </View>
        ) : codes.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialIcons name="confirmation-number" size={56} color="#8D8E90" />
            <Text style={styles.emptyTitle}>No Redeemed Codes</Text>
            <Text style={styles.emptySubtitle}>
              Enter a pricing code above to unlock special rates at charging stations.
            </Text>
          </View>
        ) : (
          <>
            <Text style={styles.listHeader}>
              Your Codes ({codes.length})
            </Text>
            {codes.map(renderCodeCard)}
          </>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>

      {renderDetailSheet()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
  },
  redeemSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
    marginBottom: 16,
  },
  sectionTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 12,
  },
  redeemRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
  },
  redeemInput: {
    flex: 1,
    backgroundColor: '#1A1D21',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: Platform.OS === 'ios' ? 12 : 10,
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    borderWidth: 1,
    borderColor: '#3A3D42',
  },
  redeemButton: {
    borderRadius: 10,
    paddingHorizontal: 18,
    paddingVertical: 0,
    margin: 0,
    justifyContent: 'center',
  },
  redeemButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  listHeader: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 10,
  },
  card: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  discountBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  discountBadgeText: {
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  codeName: {
    color: '#FFFFFF',
    fontSize: 17,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 2,
  },
  codeString: {
    color: '#40BFB4',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    letterSpacing: 1,
    marginBottom: 8,
  },
  cardMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  metaText: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  redeemedAt: {
    color: '#8D8E90',
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 4,
  },
  centered: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 20,
  },
  sheetBackground: {
    backgroundColor: '#292D33',
    borderRadius: 20,
  },
  sheetHandle: {
    backgroundColor: '#8D8E90',
    width: 36,
    height: 4,
  },
  sheetWeb: {
    maxWidth: 430,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sheetInner: {
    flex: 1,
    paddingHorizontal: 20,
  },
  chargerScrollView: {
    flex: 1,
  },
  modalTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 12,
  },
  modalBadge: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  modalBadgeText: {
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '700',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#3A3D42',
  },
  detailLabel: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  detailValue: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
    textAlign: 'right',
    flex: 1,
    marginLeft: 16,
  },
  chargersSection: {
    marginTop: 16,
    flex: 1,
  },
  chargersSectionTitle: {
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
    marginBottom: 10,
  },
  chargerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1D21',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  chargerIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#40BFB415',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  chargerDetails: {
    flex: 1,
    marginRight: 8,
  },
  chargerName: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
  chargerAddress: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 2,
  },
  chargerStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
    gap: 5,
  },
  chargerAvailDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  chargerAvailText: {
    fontSize: 11,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '600',
  },
});
